import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// Zwei Versionen der Seite in einem Projekt.
// Livefassung: REACT_APP_SITE_VERSION=1, 2, 3 oder 4 legt die Version fest. Die Vorschau fehlt dann.
// Ein Produktionsbuild ohne diese Variable blendet die Vorschau ebenfalls aus.
// Netlify-Test holt sie mit REACT_APP_PREVIEW_TOOLS=1 zurück. Lokal im Dev-Server bleibt sie sichtbar.
// Adresszusatz ?v=1|2|3|4 wählt die Version beim Laden, ?embed=1 kennzeichnet die Ansicht „Nebeneinander“.

// Version 1–3 bleiben eingefroren. Version 4 ist die neue finale Linie (V3 Inhalt, V2 Optik).
// Version 5 (29.10.2026): eigenständige Landingpage Überbestände im Design von Version 4.
const VERSIONS = ["1", "2", "3", "4", "5"];
const FIXED = VERSIONS.includes(process.env.REACT_APP_SITE_VERSION) ? process.env.REACT_APP_SITE_VERSION : null;
export const PREVIEW_TOOLS = !FIXED && (process.env.NODE_ENV !== "production" || process.env.REACT_APP_PREVIEW_TOOLS === "1");

const KEY_V = "parto-version";
const KEY_R = "parto-rounded";
const params = () => { try { return new URLSearchParams(window.location.search); } catch { return new URLSearchParams(); } };
const read = (k) => { try { return window.localStorage.getItem(k); } catch { return null; } };
const write = (k, v) => { try { window.localStorage.setItem(k, v); } catch { /* ohne Speicher gilt die Wahl nur bis zum Neuladen */ } };

// Beim Start festhalten: nach Navigation im eingebetteten Fenster fehlt der Adresszusatz
const START = params();
const EMBED = START.get("embed") === "1";

// Aktuelle Version auch außerhalb von React (z. B. src/i18n/localePath.js für die englischen Adressen von Version 4)
let CURRENT = "2";
export const getVersion = () => CURRENT;

const VersionContext = createContext({ version: "2", setVersion: () => {}, rounded: false, setRounded: () => {}, embed: false });

export const VersionProvider = ({ children }) => {
    const p = START;
    const embed = EMBED;
    const [version, setV] = useState(() => {
        if (FIXED) return FIXED;
        const q = p.get("v");
        // Gewählte Version merken, damit sie auch auf weiteren Seiten gilt (nicht im Nebeneinander)
        if (VERSIONS.includes(q)) { if (!EMBED) write(KEY_V, q); return q; }
        const s = read(KEY_V);
        return VERSIONS.includes(s) ? s : "2";
    });
    const [rounded, setR] = useState(() => {
        if (FIXED) return false;
        if (p.get("r") === "1") { if (!EMBED) write(KEY_R, "1"); return true; }
        if (p.get("r") === "0") { if (!EMBED) write(KEY_R, "0"); return false; }
        return read(KEY_R) === "1";
    });

    CURRENT = version;
    const setVersion = useCallback((v) => { CURRENT = v; setV(v); if (!embed) write(KEY_V, v); }, [embed]);
    const setRounded = useCallback((r) => { setR(r); if (!embed) write(KEY_R, r ? "1" : "0"); }, [embed]);

    useEffect(() => {
        const root = document.documentElement;
        root.classList.toggle("rl-rounded", rounded);
        // Version 5 nutzt die Stile von Version 4 (html[data-site-version="4"]); data-site-edition nennt die echte Version
        root.dataset.siteVersion = version === "5" ? "4" : version;
        root.dataset.siteEdition = version;
    }, [rounded, version]);

    const value = useMemo(() => ({ version, setVersion, rounded, setRounded, embed }), [version, setVersion, rounded, setRounded, embed]);
    return <VersionContext.Provider value={value}>{children}</VersionContext.Provider>;
};

export const useVersion = () => useContext(VersionContext);
